// src/hooks/useCatalogCosts.js — doced-8f3a61c2 (Document editor, Stage 5g: the Profit card)
//
// Today's catalog cost for a set of catalog ids, from the getCatalogCosts callable (Owner / Admin / Manager
// only — the server refuses everyone else). Asked once when the card opens and again only when the SET of ids
// changes (a sorted, joined key), in chunks of 200 (the callable's limit). A slower, older answer never
// overwrites a newer one.
//
// HARD RULE (Marcus, 2026-10-30): only DocumentProfitCard uses this, and only for high-level roles —
// `enabled` false means the server is never called. noCostLeak.test.js fails if anything else imports it.

import { useEffect, useRef, useState } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../firebase/config';

const CHUNK = 200;

export default function useCatalogCosts(serviceIds, enabled) {
    const key = enabled && Array.isArray(serviceIds) ? serviceIds.join('|') : '';
    const [state, setState] = useState({ costs: {}, loading: false, error: false });
    const [nonce, setNonce] = useState(0);
    const seq = useRef(0);
    useEffect(() => {
        if (!enabled || !key) { setState({ costs: {}, loading: false, error: false }); return undefined; }
        const mine = ++seq.current;
        const ids = key.split('|');
        setState((s) => ({ ...s, loading: true, error: false }));
        const call = httpsCallable(functions, 'getCatalogCosts');
        const chunks = [];
        for (let i = 0; i < ids.length; i += CHUNK) chunks.push(ids.slice(i, i + CHUNK));
        Promise.all(chunks.map((serviceIds) => call({ serviceIds })))
            .then((answers) => {
                if (mine !== seq.current) return;
                const costs = {};
                for (const a of answers) Object.assign(costs, (a && a.data && a.data.costs) || {});
                setState({ costs, loading: false, error: false });
            })
            .catch((e) => {
                if (mine !== seq.current) return;
                console.error('doced: catalog costs failed to load', e);
                setState({ costs: {}, loading: false, error: true });
            });
        return undefined;
    }, [enabled, key, nonce]);
    return { ...state, retry: () => setNonce((n) => n + 1) };
}
